import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as PasswordShow } from "../images/icon-eye.svg";
import { ReactComponent as PasswordHide } from "../images/icon-eye-hide.svg";
import Logo from "../images/Infomanav.svg";
import iconEmail from "../images/iconEmail.svg";
import iconKey from "../images/iconKey.svg";
import { toast } from "react-toastify";
import LoginSplash from "../component/LoginSplash";
import { hydrateAllBrokerStatistics } from "../services/dashboardStatisticsService";
import { persistAuthSessionFromPayload } from "../services/authSessionService";
import { useBrokerStatisticsStore } from "../stores/brokerStatisticsStore";

const Login = ({ setProgress }) => {
    const pmsApiBaseUrl = process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loginError, setLoginError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Add these states from the original file
    const [tabName, setTabName] = useState('Login');
    const [sharedData, setSharedData] = useState(null); // Local state instead of context
    const [signInSplashOpen, setSignInSplashOpen] = useState(false);
    const [signInSplashTo, setSignInSplashTo] = useState(null);

    useEffect(() => {
        document.title = tabName;
    }, [tabName]);

    useEffect(() => {
        if (setProgress) {
            setProgress(100);
        }
    }, [setProgress]);

    const toastOptions = {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
    };

    const showSuccessToast = (message) => {
        toast.success(message, {
            ...toastOptions,
            icon: "✅",
        });
    };

    const showErrorToast = (message) => {
        toast.error(message, {
            ...toastOptions,
            icon: "⚠️",
        });
    };

    const showWarningToast = (message) => {
        toast.warn(message, {
            ...toastOptions,
            icon: "⚠️",
        });
    };

    const showToast = (message, type = "error") => {
        if (type === "success") {
            showSuccessToast(message);
        } else if (type === "warning") {
            showWarningToast(message);
        } else {
            showErrorToast(message);
        }
    };

    // Handle input changes with error clearing
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setEmailError("");
        setLoginError("");
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setPasswordError("");
        setLoginError("");
    };

    // const brokerCatalog = [
    //     { code: "5Paisa", broker_type: 2, logo: FivePaisaLogo, alt: "5Paisa" },
    //     { code: "Groww", broker_type: 3, logo: GrowwLogo, alt: "Groww" }, // dummy type for now
    // ];
    const supportedBrokers = ["5paisa", "grow", "angelone"];
    const validateCredentials = () => {
        setLoginError("");

        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

        if (!emailRegex.test(email)) {
            showToast("Invalid email address", 'error');
            setEmailError("Invalid email address");
            return false;
        }

        if (password.trim() === "") {
            showToast("Password is required", 'error');
            setPasswordError("Password is required");
            return false;
        }
        return true;
    };



    // const handleCredentialsContinue = (e) => {
    //     e.preventDefault();
    //     if (!validateCredentials()) return;

    //     const brokerCodes = getUserBrokersForEmail();
    //     const matchedBrokers = brokerCatalog.filter((brokerItem) =>
    //         brokerCodes.includes(brokerItem.code)
    //     );

    //     if (matchedBrokers.length === 0) {
    //         showErrorToast("No broker account found for this user.");
    //         return;
    //     }

    //     setAvailableBrokers(matchedBrokers);
    //     setSelectedBrokerCode(matchedBrokers[0].code); // default first broker
    //     setBroker(matchedBrokers[0].broker_type);
    //     setLoginStep("broker");
    // };

    const normalizeBroker = (val) => String(val || "").toLowerCase().replace(/\s+/g, "");

    const handleCredentialsContinue = async (e) => {
        e.preventDefault();
        if (!validateCredentials()) return;
    
        setIsLoading(true);
    
        try {
            const response = await fetch(`${pmsApiBaseUrl}/api/v1/check-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email,
                    password,
                }),
            });
    
            const result = await response.json();
            const data = result?.data;
    
            if (data?.password_valid !== true) {
                const message = data?.message || "Invalid credentials";
                setLoginError(message);
                showErrorToast(message);
                setIsLoading(false);
                return;
            }
    
            const userBrokers = Array.isArray(data?.brokers) ? data.brokers : [];
    
            if (userBrokers.length === 0) {
                showErrorToast("No broker account found for this user.");
                setIsLoading(false);
                return;
            }

            const firstBrokerFlag = normalizeBroker(userBrokers[0]?.broker_flag);
            const isSupported = supportedBrokers.some(
                (code) => normalizeBroker(code) === firstBrokerFlag
            );

            if (!isSupported) {
                showErrorToast("No supported broker found.");
                setIsLoading(false);
                return;
            }

            localStorage.setItem("user_brokers", JSON.stringify(userBrokers));

            performLogin(firstBrokerFlag, userBrokers);
    
        } catch (error) {
            console.error("Check password error:", error);
            showErrorToast("Something went wrong. Please try again.");
            setIsLoading(false);
        }
    };





    const syncRemainingBrokerTokens = (brokers, activeBrokerFlag, userEmail) => {
        const activeFlag = normalizeBroker(activeBrokerFlag);
        const remainingFlags = (Array.isArray(brokers) ? brokers : [])
            .map((broker) => normalizeBroker(broker?.broker_flag))
            .filter((flag) => flag && flag !== activeFlag);

        remainingFlags.forEach((brokerFlag) => {
            fetch(`${pmsApiBaseUrl}/api/v1/switch-broker`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: userEmail,
                    broker_flag: brokerFlag,
                }),
            }).catch((error) => {
                console.warn(`[login] broker token sync failed for ${brokerFlag}:`, error);
            });
        });
    };

    const performLogin = (brokerFlag, brokers = []) => {
        setIsLoading(true);
        setSignInSplashOpen(true);
        setSignInSplashTo(null);

        fetch(`${pmsApiBaseUrl}/api/v1/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                email: email,
                password: password,
                broker_flag: brokerFlag
            }),
        })
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                } else {
                    throw new Error("Network response was not ok");
                }
            })
            .then(async (data) => {
                const payload = data?.data || {};
                const status = String(payload?.status || "").toLowerCase();
                const message = payload?.message || "Login failed";

                if (status === "success") {
                    const brokerType = payload?.broker_type || "";
                    const user = payload?.user || {};
                    const sharedPayload = persistAuthSessionFromPayload(payload);

                    setSharedData(sharedPayload);

                    syncRemainingBrokerTokens(
                        brokers,
                        brokerType || brokerFlag,
                        user?.email || email,
                    );

                    const activeFlag = String(brokerType || brokerFlag).trim().toLowerCase();
                    const store = useBrokerStatisticsStore.getState();
                    store.initializeFromUserBrokers();

                    try {
                        await hydrateAllBrokerStatistics({
                            pmsApiBaseUrl,
                            email: user?.email || email,
                            activeBrokerFlag: activeFlag,
                        });
                    } catch (hydrateError) {
                        console.warn("[login] broker statistics hydrate failed:", hydrateError);
                    }

                    setSignInSplashTo(
                        String(brokerType).toLowerCase() === "angelone" ? "/Dashboard" : "/Dashboard"
                    );
                } else if (status === "error") {
                    setSignInSplashOpen(false);
                    setSignInSplashTo(null);
                    setIsLoading(false);
                    setLoginError(message);
                    showErrorToast(message);
                } else {
                    setSignInSplashOpen(false);
                    setSignInSplashTo(null);
                    setIsLoading(false);
                    showErrorToast("Incorrect username or password. Please try again.")
                }
            })
            .catch((error) => {
                setSignInSplashOpen(false);
                setSignInSplashTo(null);
                setIsLoading(false);
                console.error("Error:", error);
                const errorMessage = error.message || "Network error occurred. Please try again.";
                // showToast(errorMessage, 'error');
                // setLoginError(errorMessage);
                showErrorToast(errorMessage)
            });
    };




    return (
        <div className="login-page">
            {signInSplashOpen && <LoginSplash to={signInSplashTo} />}

            <div className="login-page__backdrop" aria-hidden>
                <span className="login-page__orb login-page__orb--1" />
                <span className="login-page__orb login-page__orb--2" />
                <span className="login-page__orb login-page__orb--3" />
            </div>

            <main className="login-page__main">
                <div className="login-page__card">
                    <header className="login-page__header">
                        <img src={Logo} alt="Infomanav" className="login-page__logo" />
                        <h1 className="login-page__title">Welcome back</h1>
                        <p className="login-page__subtitle">Sign in to access your portfolio and trading workspace.</p>
                    </header>

                    <form className="login-page__form" onSubmit={handleCredentialsContinue}>
                        {loginError && (
                            <div className="auth_error login-page__error" role="alert">
                                {loginError}
                            </div>
                        )}

                        {/* Email */}
                        <div className='field_row login-page__field login-page__field--email'>
                            <label className="auth_label login-page__label" htmlFor="login-email">Email</label>
                            <div className='text_field'>
                                <div className='icon_input'><img src={iconEmail} alt="" /></div>
                                <input
                                    id="login-email"
                                    type="email"
                                    autoComplete="email"
                                    className="form-control"
                                    value={email}
                                    onChange={handleEmailChange}
                                    placeholder="Enter your email"
                                    disabled={isLoading}
                                />
                            </div>
                            {emailError && <div className="auth_error">{emailError}</div>}
                        </div>

                        {/* Password */}
                        <div className='field_row login-page__field'>
                            <label className="auth_label login-page__label" htmlFor="login-password">Password</label>
                            <div className='text_field'>
                                <div className='icon_input'><img src={iconKey} alt="" /></div>
                                <input
                                    id="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    className='form-control'
                                    value={password}
                                    placeholder="Enter your password"
                                    onChange={handlePasswordChange}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className="icon_field login-page__password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    disabled={isLoading}
                                >
                                    {showPassword ? <PasswordShow /> : <PasswordHide />}
                                </button>
                            </div>
                        </div>

                        {/* Remember me */}
                        <div className="login-page__row">
                            <div className="login-page__remember form-check">
                                <input type="checkbox" className="form-check-input" id="RememberMe" />
                                <label className="form-check-label" htmlFor="RememberMe">Remember me</label>
                            </div>
                            <Link to="/NewPassword" className="login-page__link-secondary">
                                Forgot password?
                            </Link>
                        </div>

                        <button type="submit" className="login-page__submit" disabled={isLoading}>
                            {isLoading ? "Signing in…" : "Continue"}
                        </button>

                        <div className="login-page__divider">
                            <span>New here?</span>
                        </div>

                        <div className="login-page__signup">
                            <Link to="/SignUp" className="login-page__signup-btn">Create an account</Link>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default Login;
